'use client';

import { c as _c } from "react/compiler-runtime";
import PremiumPlanSection from '@/app/[locale]/(worlds)/profile/[identifier]/components/PremiumPlanSection';
import PremiumPlanTab from '@/app/[locale]/(worlds)/profile/[identifier]/components/PremiumPlanTab';
import PremiumPlanUnavailable from '@/app/[locale]/(worlds)/profile/[identifier]/components/PremiumPlanUnavailable';
import Loader from '@/components/Loader';
import { Dialog, DialogClose, DialogOverlay, DialogPortal, DialogPrimitiveContent, DialogTitle } from '@/components/ui/dialog';
import { usePremiumPlanDialogContext } from '@/context/PremiumPlanDialogContext';
import { useUAContext } from '@/context/UAContext';
import usePremiumPlansView from '@/hooks/usePremiumPlansView';
import { cn } from '@/lib/utils';
import planListBgMobileIcon from '@/public/images/plan_list_bg_mobile.png';
import { UserProfile } from '@/services/cms';
import { X } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
type PremiumPlanDialogProps = {
  htcAccountId: string;
  userProfile: Partial<UserProfile>;
  onOpenChange?: (v: boolean) => void;
  dialogTrigger?: ReactNode;
  clickOutsideToClose?: boolean;
  withCloseButton?: boolean;
};
const styles = {
  overlay: `fixed inset-0 bg-[#000000B2]`,
  content: `fixed top-[72px] md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-navigation md:z-overlay flex w-dvw h-[calc(100dvh-72px)] md:w-[min(90dvw,836px)] md:h-[min(85dvh,640px)] flex-col overflow-hidden p-0 shadow-xl md:rounded-2xl md:bg-[rgba(33,42,57,0.40)] backdrop-blur-[50px] focus-visible:outline-none`,
  header: `text-vive-white sticky top-0 z-interactive flex md:flex-col items-center justify-between md:justify-center px-4 pt-4 pb-4 md:pt-4 md:pb-6 md:px-4`,
  title: `text-center text-white`,
  close: `focus:ring-vive-primary-30 hover:stroke-vive-white active:stroke-vive-primary-30 md:absolute md:right-4 inline-flex size-6 items-center justify-center rounded-sm transition-colors outline-none focus:ring-2 `,
  body: `dialog-scrollbar flex flex-col gap-y-6 overflow-y-auto mb-7 md:mb-4 md:mx-4 mx-3 h-full`
};
const PremiumPlanDialog = t0 => {
  const $ = _c(81);
  const {
    htcAccountId,
    userProfile,
    clickOutsideToClose: t1,
    withCloseButton: t2
  } = t0;
  const clickOutsideToClose = t1 === undefined ? false : t1;
  const withCloseButton = t2 === undefined ? true : t2;
  const {
    isMobile
  } = useUAContext();
  const {
    display_name: displayName
  } = userProfile;
  const {
    isOpen,
    closeDialog
  } = usePremiumPlanDialogContext();
  const [activePlanId, setActivePlanId] = useState();
  const [sessionKey, setSessionKey] = useState(null);
  const [roomsState, setRoomsState] = useState(null);
  const {
    premiumPlans,
    isLoading,
    isError,
    isEmpty
  } = usePremiumPlansView(htcAccountId, isOpen);
  let t3;
  if ($[0] !== activePlanId || $[1] !== premiumPlans) {
    let t4;
    if ($[3] !== activePlanId) {
      t4 = p => p.id === activePlanId;
      $[3] = activePlanId;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t3 = premiumPlans.find(t4);
    $[0] = activePlanId;
    $[1] = premiumPlans;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const selectedPlan = t3;
  const canFetchVoucherRooms = Boolean(isOpen && sessionKey && selectedPlan?.voucherId);
  const currentVoucherId = selectedPlan?.voucherId ?? null;
  const showRoomsUnavailable = !!canFetchVoucherRooms && roomsState?.voucherId === currentVoucherId && !roomsState.loading && (roomsState.empty || roomsState.error);
  let t4;
  if ($[5] !== isMobile) {
    t4 = id => setActivePlanId(prev => isMobile ? prev === id ? null : id : id);
    $[5] = isMobile;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleSelect = t4;
  let t5;
  if ($[7] !== activePlanId || $[8] !== isMobile || $[9] !== isOpen || $[10] !== premiumPlans[0] || $[11] !== premiumPlans.length) {
    t5 = () => {
      if (!isOpen) {
        setActivePlanId(null);
      }
      if (!isMobile && !activePlanId && premiumPlans.length > 0) {
        setActivePlanId(premiumPlans[0].id);
      }
    };
    $[7] = activePlanId;
    $[8] = isMobile;
    $[9] = isOpen;
    $[10] = premiumPlans[0];
    $[11] = premiumPlans.length;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== activePlanId || $[14] !== isMobile || $[15] !== isOpen || $[16] !== premiumPlans) {
    t6 = [premiumPlans, activePlanId, isOpen, isMobile];
    $[13] = activePlanId;
    $[14] = isMobile;
    $[15] = isOpen;
    $[16] = premiumPlans;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[18] !== isOpen) {
    t7 = () => {
      if (isOpen) {
        setSessionKey(crypto.randomUUID());
      } else {
        setSessionKey(null);
      }
    };
    t8 = [isOpen];
    $[18] = isOpen;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[21] !== isMobile || $[22] !== isOpen) {
    t9 = () => {
      if (!isMobile) {
        return;
      }
      if (!isOpen) {
        return;
      }
      const body = document.body;
      body.classList.add("overflow-hidden");
      return () => {
        body.classList.remove("overflow-hidden");
      };
    };
    t10 = [isMobile, isOpen];
    $[21] = isMobile;
    $[22] = isOpen;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  useEffect(t9, t10);
  if (!isOpen) {
    return null;
  }
  const t11 = !isMobile;
  let t12;
  if ($[25] !== isMobile) {
    t12 = !isMobile && <DialogOverlay className={styles.overlay} />;
    $[25] = isMobile;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = isMobile && "bg-cover bg-center bg-no-repeat";
  let t14;
  if ($[27] !== t13) {
    t14 = cn(styles.content, t13);
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== isMobile) {
    t15 = isMobile ? {
      backgroundImage: `linear-gradient(rgba(33,42,57,0.40), rgba(33,42,57,0.40)), url(${planListBgMobileIcon.src})`
    } : undefined;
    $[29] = isMobile;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = clickOutsideToClose ? undefined : _temp2;
  const t17 = clickOutsideToClose ? undefined : _temp3;
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cn(styles.header);
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = isMobile ? "header1-omni-styles" : "subheader2-omni-styles";
  let t20;
  if ($[32] !== t19) {
    t20 = cn(styles.title, t19);
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== displayName || $[35] !== t20) {
    t21 = <DialogTitle className={t20}>{displayName}</DialogTitle>;
    $[34] = displayName;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== closeDialog || $[38] !== withCloseButton) {
    t22 = withCloseButton && <DialogClose className={styles.close} onClick={closeDialog} aria-label="close"><X className="size-7 transition-all" /></DialogClose>;
    $[37] = closeDialog;
    $[38] = withCloseButton;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t21 || $[41] !== t22) {
    t23 = <div className={t18}>{t21}{t22}</div>;
    $[40] = t21;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  const t24 = !isMobile && "md:overflow-hidden";
  let t25;
  if ($[43] !== t24) {
    t25 = cn(styles.body, t24);
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== isLoading) {
    t26 = isLoading && <div className="flex h-full w-full items-center justify-center py-10"><Loader /></div>;
    $[45] = isLoading;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== isEmpty || $[48] !== isError || $[49] !== isLoading || $[50] !== showRoomsUnavailable) {
    t27 = (!isLoading && (!!isError || !!isEmpty) || showRoomsUnavailable) && <PremiumPlanUnavailable />;
    $[47] = isEmpty;
    $[48] = isError;
    $[49] = isLoading;
    $[50] = showRoomsUnavailable;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== activePlanId || $[53] !== canFetchVoucherRooms || $[54] !== handleSelect || $[55] !== isEmpty || $[56] !== isLoading || $[57] !== isMobile || $[58] !== premiumPlans || $[59] !== sessionKey || $[60] !== showRoomsUnavailable) {
    t28 = !isLoading && !isEmpty && !showRoomsUnavailable && <div className={cn("grid grid-cols-1 gap-6 md:grid-cols-[216px_1fr]", !isMobile && "md:h-full md:grid-rows-[minmax(0,1fr)]")}>{!isMobile && <div className="dialog-scrollbar h-full min-h-0 overflow-y-auto"><PremiumPlanTab plans={premiumPlans} activeId={activePlanId} onSelect={handleSelect} /></div>}<PremiumPlanSection plans={premiumPlans} activePlanId={activePlanId} onSelect={handleSelect} canFetchVoucherRooms={canFetchVoucherRooms} sessionKey={sessionKey} onRoomsResult={setRoomsState} /></div>;
    $[52] = activePlanId;
    $[53] = canFetchVoucherRooms;
    $[54] = handleSelect;
    $[55] = isEmpty;
    $[56] = isLoading;
    $[57] = isMobile;
    $[58] = premiumPlans;
    $[59] = sessionKey;
    $[60] = showRoomsUnavailable;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t25 || $[63] !== t26 || $[64] !== t27 || $[65] !== t28) {
    t29 = <div className={t25}>{t26}{t27}{t28}</div>;
    $[62] = t25;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t14 || $[68] !== t15 || $[69] !== t16 || $[70] !== t17 || $[71] !== t23 || $[72] !== t29) {
    t30 = <DialogPrimitiveContent tabIndex={-1} className={t14} style={t15} onOpenAutoFocus={_temp} onInteractOutside={t16} onEscapeKeyDown={t17}>{t23}{t29}</DialogPrimitiveContent>;
    $[67] = t14;
    $[68] = t15;
    $[69] = t16;
    $[70] = t17;
    $[71] = t23;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== t12 || $[75] !== t30) {
    t31 = <DialogPortal>{t12}{t30}</DialogPortal>;
    $[74] = t12;
    $[75] = t30;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== isOpen || $[78] !== t11 || $[79] !== t31) {
    t32 = <Dialog open={isOpen} modal={t11}>{t31}</Dialog>;
    $[77] = isOpen;
    $[78] = t11;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  return t32;
};
export default PremiumPlanDialog;
function _temp(e) {
  e.preventDefault();
  const target = e.currentTarget as HTMLElement;
  target.focus();
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp3(e_1) {
  return e_1.preventDefault();
}